import { SSO_AUTH_MODE } from '@core/runtime-config'
import { logoutSso } from '@core/sso'
import {
  forgotPassword as forgotPasswordRequest,
  login as loginRequest,
  logout as logoutRequest,
  resetPassword as resetPasswordRequest,
} from '@shared/api/authentication'
import type { UserProfileDto } from '@shared/api/generated/model'
import { requiredString, requireEnvelopeData } from '@shared/lib/api/contract'
import { Permission, type User, UserRole } from '@shared/types/global.types'
import type { LoginCredentials, LoginResponse } from '../types/auth.types'

export function normalizeRole(role?: string | number | null): UserRole {
  if (role === 2 || role === '2' || role === UserRole.ADMIN) return UserRole.ADMIN
  if (role === 1 || role === '1' || role === UserRole.MANAGER) return UserRole.MANAGER
  return UserRole.USER
}

export function getPermissionsByRole(role?: string | number | null) {
  switch (normalizeRole(role)) {
    case UserRole.ADMIN:
      return [
        Permission.READ,
        Permission.WRITE,
        Permission.DELETE,
        Permission.PUBLISH,
        Permission.MANAGE_USERS,
        Permission.VIEW_ANALYTICS,
        Permission.SEND_NOTIFICATIONS,
      ]
    case UserRole.MANAGER:
      return [Permission.READ, Permission.WRITE, Permission.PUBLISH, Permission.VIEW_ANALYTICS]
    default:
      return [Permission.READ]
  }
}

export function mapAuthenticatedUser(user: UserProfileDto): User {
  const role = normalizeRole(user.role)
  return {
    id: requiredString(user.id, 'auth.user.id'),
    name: requiredString(user.name, 'auth.user.name'),
    email: requiredString(user.email, 'auth.user.email'),
    role,
    permissions: getPermissionsByRole(role),
  }
}

export async function login(credentials: LoginCredentials): Promise<LoginResponse> {
  const payload = requireEnvelopeData(await loginRequest(credentials), 'login')
  const user = requireEnvelopeData({ data: payload.user }, 'usuário autenticado')
  return { user: mapAuthenticatedUser(user) }
}

export async function logout(): Promise<void> {
  if (SSO_AUTH_MODE) {
    await logoutSso()
    return
  }
  await logoutRequest()
}

export async function forgotPassword(email: string): Promise<void> {
  await forgotPasswordRequest({ email })
}

export async function resetPassword(token: string, newPassword: string): Promise<void> {
  await resetPasswordRequest({ token, newPassword })
}
